import useCurrencyApi from "views/Settings/General/components/Formats/Currency/hooks/useCurrencyApi";
import CustomCurrencyInput from "components/CustomCurrencyInput";
import UpdateProductPrice from "./UpdateProductPrice";
import ProductMargin from "./ProductMargin";
import { Col, Form, Input, Row, Select } from "antd";
import { ApolloQueryResult } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffect } from "react";

interface ProductPricesFormListProps {
  refetchProduct?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetProductResponse>>;
  refetchProducts?: () => void;
  isOpen?: boolean;
}

const PRICE_HEADERS = [
  { label: "Currency", key: "currency", span: 4, className: "required" },
  { label: "Purchase price", key: "buyPriceTotal", span: 5 },
  { label: "Margin rate", key: "margin", span: 5 },
  { label: "Unit price before tax", key: "priceNet", span: 5 },
  { label: "Unit price including tax", key: "priceIncludingVat", span: 5 },
];

export default function ProductPricesFormList({
  refetchProduct,
  refetchProducts,
  isOpen = true,
}: ProductPricesFormListProps): React.ReactElement {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const form = Form.useFormInstance();
  const { listCurrenciesOptions, currenciesOption, listCurrenciesOptionsLoading } =
    useCurrencyApi();

  useEffect(() => {
    if (urlParams?.companyId && isOpen) {
      listCurrenciesOptions({
        variables: {
          filter: { companyId: urlParams?.companyId, isActive: true },
        },
      });
    }
  }, [urlParams?.companyId, isOpen]);

  useEffect(() => {
    if (currenciesOption?.length > 0) {
      const currentPrices = form.getFieldValue("prices");

      if (!currentPrices || currentPrices.length === 0) {
        const defaultPrices = currenciesOption.map(item => ({
          currency: item?.value,
          extraInfo: {
            currencyCode: item?.label,
            decimalPlaces: item?.decimalPlaces,
            isMainCurrency: item?.isMainCurrency,
          },
          buyPriceTotal: undefined,
          priceNet: undefined,
          priceIncludingVat: undefined,
        }));

        form.setFieldValue("prices", defaultPrices);
      }
    }
  }, [currenciesOption, form]);

  return (
    <Col span={24}>
      <Row gutter={12} className="header-row">
        {PRICE_HEADERS.map((headerItem, index) => (
          <Col
            span={headerItem.span}
            className={headerItem.className}
            key={`entry-header-${headerItem.key || index}`}
          >
            {t(headerItem.label)}
          </Col>
        ))}
      </Row>
      <Form.List name="prices">
        {fields =>
          fields.map(field => {
            const extraInfo = form.getFieldValue(["prices", field.name, "extraInfo"]);
            const currencyCode = extraInfo?.currencyCode;

            if (field.name !== 0) return null;

            return (
              <Row gutter={12} key={field.name} className="form-row__prices">
                <Form.Item name={[field.name, "isMainCurrency"]} hidden />
                <Form.Item name={[field.name, "extraInfo"]} hidden>
                  <Input disabled />
                </Form.Item>
                <Col span={4}>
                  <Form.Item name={[field.name, "currency"]}>
                    <Select
                      loading={listCurrenciesOptionsLoading}
                      options={currenciesOption}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <UpdateProductPrice
                    refetchProduct={refetchProduct}
                    refetchProducts={refetchProducts}
                    type="buyPriceTotal"
                    name={field.name}
                  />
                </Col>
                <Col span={5}>
                  <ProductMargin
                    refetchProduct={refetchProduct}
                    refetchProducts={refetchProducts}
                    name={field.name}
                  />
                </Col>
                <Col span={5}>
                  <UpdateProductPrice
                    refetchProduct={refetchProduct}
                    refetchProducts={refetchProducts}
                    type="priceNet"
                    name={field.name}
                  />
                </Col>
                <Col span={5}>
                  <Form.Item name={[field.name, "priceIncludingVat"]}>
                    <CustomCurrencyInput
                      disabled
                      decimalPlaces={extraInfo?.decimalPlaces}
                      formatter={(value: unknown) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      suffix={currencyCode}
                    />
                  </Form.Item>
                </Col>
              </Row>
            );
          })
        }
      </Form.List>
    </Col>
  );
}
